import { registerPlugin } from '@capacitor/core';

import type {
  AddPageOptions,
  AnalyticsSubscriber,
  BarcodeDocumentFormat,
  BarcodeDocumentParserResult,
  BarcodeItem,
  BarcodeMappedData,
  BarcodeScannerConfiguration,
  BarcodeScannerResult,
  BarcodeScannerScreenConfiguration,
  BarcodeScannerUiResult,
  BufferImageLoadOptions,
  CheckScannerConfiguration,
  CheckScannerScreenConfiguration,
  CheckScannerUiResult,
  CheckScanningResult,
  CreateDocumentOptions,
  CreditCardScannerConfiguration,
  CreditCardScannerScreenConfiguration,
  CreditCardScannerUiResult,
  CreditCardScanningResult,
  CroppingConfiguration,
  DeepPartial,
  DocumentData,
  DocumentDataExtractionResult,
  DocumentDataExtractorConfiguration,
  DocumentDataExtractorScreenConfiguration,
  DocumentDataExtractorUiResult,
  DocumentQualityAnalyzerConfiguration,
  DocumentQualityAnalyzerResult,
  DocumentScannerConfiguration,
  DocumentScanningFlow,
  DocumentScanningResult,
  DocumentVerificationReport,
  EncodeImageOptions,
  GenericDocument,
  ImageInfo,
  ImageRef,
  ImageRefPoolSnapshot,
  ImageRotation,
  LicenseInfo,
  ModifyPageOptions,
  MrzScannerConfiguration,
  MrzScannerResult,
  MrzScannerScreenConfiguration,
  MrzScannerUiResult,
  OcrConfigsResult,
  OcrConfiguration,
  Page,
  ParametricFilter,
  PathImageLoadOptions,
  PdfConfiguration,
  PdfExtractorOptions,
  PerformOcrResult,
  Point,
  ResultWrapper,
  SaveImageOptions,
  SdkConfiguration,
  SerializedImageInput,
  TextPatternScannerScreenConfiguration,
  TextPatternScannerUiResult,
  TiffGeneratorParameters,
  VinScannerScreenConfiguration,
  VinScannerUiResult,
} from '../types';

type WrappedResult<T> = { result: T };

/**
 * @internal
 * @hidden
 */
export interface _ScanbotSDKCapacitorInternalPlugin {
  initialize(configuration: SdkConfiguration): Promise<DeepPartial<LicenseInfo>>;

  getLicenseInfo(): Promise<DeepPartial<LicenseInfo>>;

  getOcrConfigs(): Promise<DeepPartial<OcrConfigsResult>>;

  cleanupStorage(): Promise<void>;

  setAnalyticsServiceCallback(args: Record<string, never>, subscriber: AnalyticsSubscriber): Promise<void>;

  removeAnalyticsServiceCallback(): Promise<void>;

  mockCamera(params: { imageFileUri: string; captureFileUri?: string; refreshOnEachFrame?: boolean }): Promise<void>;

  //#region Barcode APIs

  scanBarcodesFromImage(params: {
    image: SerializedImageInput;
    configuration: BarcodeScannerConfiguration;
  }): Promise<DeepPartial<BarcodeScannerResult>>;

  scanBarcodesFromPdf(params: {
    pdfFileUri: string;
    configuration: BarcodeScannerConfiguration;
  }): Promise<DeepPartial<BarcodeScannerResult>>;

  startBarcodeScanner(
    configuration: BarcodeScannerScreenConfiguration,
  ): Promise<ResultWrapper<DeepPartial<BarcodeScannerUiResult>>>;

  parseBarcodeDocument(params: {
    rawText: string;
    acceptedFormats?: BarcodeDocumentFormat[];
  }): Promise<DeepPartial<BarcodeDocumentParserResult>>;

  // params are not used, but if we set the callback as first param, deprecated warning is shown
  registerBarcodeItemMapperCallback(params: Record<string, never>, callback: (barcodeItem: BarcodeItem) => void): void;

  onBarcodeItemMapper(params: { barcodeItemUuid: string; barcodeMappedData?: BarcodeMappedData | null }): void;

  //#endregion Barcode APIs

  //#region Check APIs

  scanCheckFromImage(params: {
    image: SerializedImageInput;
    configuration: CheckScannerConfiguration;
  }): Promise<DeepPartial<CheckScanningResult>>;

  startCheckScanner(
    configuration: CheckScannerScreenConfiguration,
  ): Promise<ResultWrapper<DeepPartial<CheckScannerUiResult>>>;

  //#endregion Check APIs

  //#region Credit Card APIs

  scanCreditCardFromImage(params: {
    image: SerializedImageInput;
    configuration: CreditCardScannerConfiguration;
  }): Promise<DeepPartial<CreditCardScanningResult>>;

  startCreditCardScanner(
    configuration: CreditCardScannerScreenConfiguration,
  ): Promise<ResultWrapper<DeepPartial<CreditCardScannerUiResult>>>;

  //#endregion Credit Card APIs

  //#region Document APIs

  createDocumentFromImages(params: {
    images?: SerializedImageInput[];
    options?: CreateDocumentOptions;
  }): Promise<DeepPartial<DocumentData>>;

  createDocumentFromLegacyPages(params: {
    pages: Page[];
    documentImageSizeLimit?: number;
  }): Promise<DeepPartial<DocumentData>>;

  createDocumentFromPdf(params: {
    pdfFileUri: string;
    options?: CreateDocumentOptions;
  }): Promise<DeepPartial<DocumentData>>;

  documentExists(params: { documentUuid: string }): Promise<WrappedResult<boolean>>;

  loadDocument(params: { documentUuid: string }): Promise<DeepPartial<DocumentData>>;

  getStoredDocumentUuids(): Promise<WrappedResult<string[]>>;

  cloneDocument(params: { documentUuid: string }): Promise<DeepPartial<DocumentData>>;

  deleteDocument(params: { documentUuid: string }): Promise<void>;

  deleteAllDocuments(): Promise<void>;

  addPages(params: {
    documentUuid: string;
    images: SerializedImageInput[];
    options?: AddPageOptions;
  }): Promise<DeepPartial<DocumentData>>;

  movePage(params: { documentUuid: string; fromIndex: number; toIndex: number }): Promise<DeepPartial<DocumentData>>;

  modifyPage(params: {
    documentUuid: string;
    pageUuid: string;
    options?: ModifyPageOptions;
  }): Promise<DeepPartial<DocumentData>>;

  removePagesFromDocument(params: { documentUuid: string; pageUuids: string[] }): Promise<DeepPartial<DocumentData>>;

  removeAllPages(params: { documentUuid: string }): Promise<DeepPartial<DocumentData>>;

  startDocumentScanner(configuration: DocumentScanningFlow): Promise<ResultWrapper<DeepPartial<DocumentData>>>;

  startCroppingScreen(configuration: CroppingConfiguration): Promise<ResultWrapper<DeepPartial<DocumentData>>>;

  scanDocumentFromImage(params: {
    image: SerializedImageInput;
    configuration: DocumentScannerConfiguration;
  }): Promise<DocumentScanningResult>;

  analyzeDocumentQualityOnImage(params: {
    image: SerializedImageInput;
    configuration: DocumentQualityAnalyzerConfiguration;
  }): Promise<DeepPartial<DocumentQualityAnalyzerResult>>;

  //#endregion Document APIs

  //#region Legacy Page APIs

  removeLegacyPage(params: { page: Page }): Promise<void>;

  removeAllLegacyPages(): Promise<void>;

  setDocumentImageOnLegacyPage(params: { imageFileUri: string; page: Page }): Promise<Page>;

  refreshImageUrisOnLegacyPage(params: { pages: Page[] }): Promise<WrappedResult<Page[]>>;

  //#endregion Legacy Page APIs

  //#region DDE APIs

  extractDocumentDataFromImage(params: {
    image: SerializedImageInput;
    configuration: DocumentDataExtractorConfiguration;
  }): Promise<DeepPartial<DocumentDataExtractionResult>>;

  verifyDocument(params: { documentParts: GenericDocument[] }): Promise<DeepPartial<DocumentVerificationReport>>;

  startDDEScreen(
    configuration: DocumentDataExtractorScreenConfiguration,
  ): Promise<ResultWrapper<DeepPartial<DocumentDataExtractorUiResult>>>;

  //#endregion DDE APIs

  //#region Image Operations APIs

  readImageData(params: { imageFileUri: string }): Promise<WrappedResult<string>>;

  rotateImageFile(params: {
    imageFileUri: string;
    rotation: ImageRotation;
    overwrite?: boolean;
    saveOptions?: SaveImageOptions;
  }): Promise<WrappedResult<string>>;

  rotateImage(params: { image: SerializedImageInput; rotation: ImageRotation }): Promise<DeepPartial<ImageRef>>;

  applyFiltersOnImageFile(params: {
    imageFileUri: string;
    filters: ParametricFilter[];
    overwrite?: boolean;
    saveOptions?: SaveImageOptions;
  }): Promise<WrappedResult<string>>;

  applyFiltersOnImage(params: {
    image: SerializedImageInput;
    filters: ParametricFilter[];
  }): Promise<DeepPartial<ImageRef>>;

  resizeImageFile(params: {
    imageFileUri: string;
    maxSize: number;
    overwrite?: boolean;
    saveOptions?: SaveImageOptions;
  }): Promise<WrappedResult<string>>;

  resizeImage(params: { image: SerializedImageInput; maxSize: number }): Promise<DeepPartial<ImageRef>>;

  cropImageFile(params: {
    imageFileUri: string;
    polygon: Point[];
    overwrite?: boolean;
    saveOptions?: SaveImageOptions;
  }): Promise<WrappedResult<string>>;

  cropImage(params: { image: SerializedImageInput; polygon: Point[] }): Promise<DeepPartial<ImageRef>>;

  //#endregion Image Operations APIs

  //#region MRZ APIs

  scanMrzFromImage(params: {
    image: SerializedImageInput;
    configuration: MrzScannerConfiguration;
  }): Promise<DeepPartial<MrzScannerResult>>;

  startMrzScanner(
    configuration: MrzScannerScreenConfiguration,
  ): Promise<ResultWrapper<DeepPartial<MrzScannerUiResult>>>;

  //#endregion MRZ APIs

  //#region Ocr Engine APIs

  recognizeOcrOnImages(params: {
    images: SerializedImageInput[];
    configuration?: OcrConfiguration;
  }): Promise<DeepPartial<PerformOcrResult>>;

  recognizeOcrOnDocument(params: {
    documentUuid: string;
    configuration?: OcrConfiguration;
  }): Promise<DeepPartial<PerformOcrResult>>;

  //#endregion Ocr Engine APIs

  //#region Pdf Generator APIs

  generatePdfFromImages(params: {
    images: SerializedImageInput[];
    pdfConfiguration: PdfConfiguration;
    ocrConfiguration?: OcrConfiguration;
  }): Promise<WrappedResult<string>>;

  generatePdfFromDocument(params: {
    documentUuid: string;
    pdfConfiguration: PdfConfiguration;
    ocrConfiguration?: OcrConfiguration;
  }): Promise<WrappedResult<string>>;

  //#endregion Pdf Generator APIs

  //#region Pdf Extractor APIs

  extractImageFilesFromPdf(params: {
    pdfFileUri: string;
    options?: PdfExtractorOptions;
  }): Promise<WrappedResult<string[]>>;

  //#endregion Pdf Extractor APIs

  //#region Text Pattern APIs

  startTextPatternScanner(
    configuration: TextPatternScannerScreenConfiguration,
  ): Promise<ResultWrapper<DeepPartial<TextPatternScannerUiResult>>>;

  //#endregion Text Pattern APIs

  //#region Tiff Generator APIs

  generateTiffFromImages(params: {
    images: SerializedImageInput[];
    tiffGeneratorParameters: TiffGeneratorParameters;
  }): Promise<WrappedResult<string>>;

  generateTiffFromDocument(params: {
    documentUuid: string;
    tiffGeneratorParameters: TiffGeneratorParameters;
  }): Promise<WrappedResult<string>>;

  //#endregion Tiff Generator APIs

  //#region VIN APIs

  startVinScanner(
    configuration: VinScannerScreenConfiguration,
  ): Promise<ResultWrapper<DeepPartial<VinScannerUiResult>>>;

  //#endregion VIN APIs

  //#region ImageRef APIs

  imageRefSerialize(params: { uniqueId: string }): Promise<WrappedResult<boolean>>;

  imageRefDeserialize(params: { uniqueId: string }): Promise<WrappedResult<boolean>>;

  imageRefFromImageFileUri(params: { uri: string; options: PathImageLoadOptions }): Promise<WrappedResult<string>>;

  imageRefFromEncodedBuffer(params: {
    buffer: string;
    options: BufferImageLoadOptions;
  }): Promise<WrappedResult<string>>;

  imageRefHibernate(params: { uniqueId: string }): Promise<void>;

  imageRefClear(params: { uniqueId: string }): Promise<void>;

  imageRefInfo(params: { uniqueId: string }): Promise<DeepPartial<ImageInfo>>;

  imageRefSaveImage(params: {
    uniqueId: string;
    path: string;
    options: SaveImageOptions;
  }): Promise<WrappedResult<boolean>>;

  imageRefEncodeImage(params: { uniqueId: string; options: EncodeImageOptions }): Promise<WrappedResult<string>>;

  imageRefRelease(params: { uniqueId: string }): Promise<WrappedResult<boolean>>;

  makeSnapshot(): Promise<DeepPartial<ImageRefPoolSnapshot>>;

  imageRefReleaseAll(): Promise<void>;

  //#endregion ImageRef APIs
}

/**
 * @internal
 * @hidden
 */
export const ScanbotSDKPlugin = registerPlugin<_ScanbotSDKCapacitorInternalPlugin>('ScanbotSDKCapacitor', {});
